// pages/LandingPage/LandingPage.js
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

/* ─── asset helpers ─────────────────────────────────────── */
const p = (path) => `${process.env.PUBLIC_URL}${path}`;

const QUOTE_IMGS = [
  "pic1 (2).jpg","pic2 (2).jpg","pic3 (2).jpg","pic4 (2).jpg","pic5 (2).jpg",
  "pic6 (2).jpg","pic7 (2).jpg","pic8 (2).jpg","pic9 (2).jpg","pic11 (2).jpg","pic12 (2).jpg",
].map((f) => p(`/quotebackground/${encodeURIComponent(f)}`));

const PROFILE_IMGS = [
  "Make up.jpg",
  "download (10).jpg","download (11).jpg","download (12).jpg","download (13).jpg",
  "download (17).jpg","download (18).jpg","download (19).jpg","download (20).jpg",
].map((f) => p(`/profilebackground/${encodeURIComponent(f)}`));

const STAY_IMG = p(`/Images/${encodeURIComponent("𝒜𝓁𝓁 𝓎ℴ𝓊 𝒽𝒶𝒹 𝓉ℴ 𝒹ℴ 𝓌𝒶𝓈 𝓈𝓉𝒶𝓎 ♥.jpg")}`);

/* ─── hooks ──────────────────────────────────────────────── */
function useFastCrossfade(images, interval = 380) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), interval);
    return () => clearInterval(t);
  }, [images.length, interval]);
  return idx;
}

function useInView(threshold = 0.35) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return mobile;
}

/* ═══════════════════════════════════════════════════════════
   CURSOR — unseen.co style fluid lens
═══════════════════════════════════════════════════════════ */
function UnseenCursor() {
  const outerRef = useRef(null);
  const dotRef   = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(pointer:coarse)").matches) return;
    let ox = -300, oy = -300, tx = -300, ty = -300, raf;
    const SIZE = 110;
    const onMove = (e) => { tx = e.clientX; ty = e.clientY; };
    window.addEventListener("mousemove", onMove);
    const loop = () => {
      ox += (tx - ox) * 0.072;
      oy += (ty - oy) * 0.072;
      if (outerRef.current) outerRef.current.style.transform = `translate(${ox - SIZE / 2}px,${oy - SIZE / 2}px)`;
      if (dotRef.current)   dotRef.current.style.transform   = `translate(${tx - 4}px,${ty - 4}px)`;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { window.removeEventListener("mousemove", onMove); cancelAnimationFrame(raf); };
  }, []);

  return (
    <>
      <div ref={outerRef} className="hidden md:block" style={{
        position:"fixed",top:0,left:0,width:110,height:110,borderRadius:"50%",zIndex:9998,pointerEvents:"none",
        background:"rgba(255,255,255,0.04)",backdropFilter:"blur(3px) brightness(1.15) saturate(1.8)",
        WebkitBackdropFilter:"blur(3px) brightness(1.15) saturate(1.8)",
        border:"1.5px solid rgba(255,255,255,0.18)",boxShadow:"0 0 0 1px rgba(255,255,255,0.06) inset",
        mixBlendMode:"screen",willChange:"transform",
      }}/>
      <div ref={dotRef} className="hidden md:block" style={{
        position:"fixed",top:0,left:0,width:8,height:8,borderRadius:"50%",zIndex:9999,
        pointerEvents:"none",background:"#fff",mixBlendMode:"difference",willChange:"transform",
      }}/>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   CURTAIN REVEAL — amber.framer.media style
═══════════════════════════════════════════════════════════ */
function CurtainReveal({ children, color = "#0a0e1a" }) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setOpen(true); },
      { threshold: 0.12 }
    );
    if (wrapRef.current) obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={wrapRef} style={{ position:"relative", height:"100%", overflow:"hidden" }}>
      {children}
      <motion.div
        initial={{ y:"0%" }} animate={open ? { y:"-101%" } : { y:"0%" }}
        transition={{ duration:0.95, ease:[0.76,0,0.24,1] }}
        style={{ position:"absolute",inset:0,background:color,zIndex:40,pointerEvents:"none",willChange:"transform" }}
      />
      <motion.div
        initial={{ y:"0%" }} animate={open ? { y:"-101%" } : { y:"0%" }}
        transition={{ duration:0.95, ease:[0.76,0,0.24,1] }}
        style={{ position:"absolute",bottom:-1,left:0,right:0,height:2,background:"rgba(0,212,255,0.5)",zIndex:41,pointerEvents:"none",willChange:"transform" }}
      />
    </div>
  );
}

/* ─── PageDots ───────────────────────────────────────────── */
function PageDots({ total, current }) {
  return (
    <div className="hidden md:flex" style={{ position:"fixed",right:20,top:"50%",transform:"translateY(-50%)",flexDirection:"column",gap:8,zIndex:200 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{ width:i===current?10:6,height:i===current?10:6,borderRadius:"50%",background:i===current?"#fff":"rgba(255,255,255,0.28)",transition:"all 0.3s ease" }}/>
      ))}
    </div>
  );
}

/* ─── ScrambleText ──────────────────────────────────────── */
const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%";
function ScrambleText({ text, inView }) {
  const [display, setDisplay] = useState(text);
  useEffect(() => {
    if (!inView) return;
    let iter = 0;
    const iv = setInterval(() => {
      setDisplay(text.split("").map((ch, i) =>
        i < iter ? ch : ch === " " ? " " : CHARS[Math.floor(Math.random() * CHARS.length)]
      ).join(""));
      if (iter >= text.length) clearInterval(iv);
      iter += 0.5;
    }, 30);
    return () => clearInterval(iv);
  }, [inView, text]);
  return <span>{display}</span>;
}

/* ─── Typewriter ────────────────────────────────────────── */
function Typewriter({ lines, speed = 55, onDone }) {
  const [rows, setRows] = useState([""]);
  const [done, setDone] = useState(false);
  useEffect(() => {
    let li = 0, ci = 0;
    const buf = [""];
    const tick = setInterval(() => {
      if (li >= lines.length) { clearInterval(tick); setDone(true); onDone?.(); return; }
      ci++;
      const upd = [...buf];
      upd[li] = lines[li].slice(0, ci);
      buf[li] = upd[li];
      setRows([...upd]);
      if (ci >= lines[li].length) { li++; ci = 0; buf.push(""); setRows([...buf, ""]); }
    }, speed);
    return () => clearInterval(tick);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div style={{ textAlign:"center", marginTop:16, minHeight:56 }}>
      {rows.map((line, i) => (
        <p key={i} style={{ color:"rgba(255,255,255,0.75)", fontSize:"clamp(0.85rem,2vw,1.15rem)", letterSpacing:"2px", margin:"4px 0" }}>
          {line}{i === rows.length - 1 && !done && <span className="animate-pulse">|</span>}
        </p>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SECTION 1 — HERO
═══════════════════════════════════════════════════════════ */
function S1Hero() {
  const [twDone, setTwDone] = useState(false);
  return (
    <section className="snap-section">
      <CurtainReveal color="#000">
        <video autoPlay loop muted playsInline style={{ position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover" }} src={p("/video/whaleV.mp4")}/>
        <div style={{ position:"absolute",inset:0,background:"rgba(0,0,0,0.38)" }}/>
        <div style={{ position:"relative",zIndex:1,height:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"0 1.5rem",textAlign:"center" }}>
          <h1 style={{ fontSize:"clamp(2.4rem,8vw,7rem)",fontWeight:900,letterSpacing:"clamp(4px,1.5vw,8px)",color:"#fff",margin:0,textShadow:"0 0 60px rgba(255,255,255,0.15)" }}>PICPROFILE</h1>
          <Typewriter lines={["connecting...", "creators, brands and communities"]} onDone={() => setTwDone(true)}/>
          <AnimatePresence>
            {twDone && (
              <motion.div initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} transition={{ duration:0.8,delay:0.2 }}
                style={{ marginTop:32,display:"flex",flexDirection:"column",alignItems:"center",gap:20 }}>
                <p style={{ color:"rgba(255,255,255,0.58)",fontSize:"clamp(0.78rem,1.8vw,1.1rem)",letterSpacing:"clamp(1px,0.5vw,3px)",textTransform:"uppercase",margin:0,padding:"0 1rem" }}>
                  Your story. Your aesthetic. Your profile.
                </p>
                <div style={{ display:"flex",gap:12,flexWrap:"wrap",justifyContent:"center",padding:"0 1rem" }}>
                  <motion.a href="/auth" whileHover={{ scale:1.06 }}
                    style={{ padding:"12px 28px",borderRadius:999,background:"#fff",color:"#000",fontWeight:700,fontSize:"clamp(0.82rem,1.5vw,0.95rem)",textDecoration:"none",letterSpacing:"1px" }}>
                    Get Started
                  </motion.a>
                  <motion.a href="#" whileHover={{ scale:1.06 }}
                    style={{ padding:"12px 28px",borderRadius:999,background:"rgba(255,255,255,0.1)",color:"#fff",fontWeight:600,fontSize:"clamp(0.82rem,1.5vw,0.95rem)",textDecoration:"none",letterSpacing:"1px",border:"1px solid rgba(255,255,255,0.25)",backdropFilter:"blur(8px)" }}>
                    Learn More
                  </motion.a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <div style={{ position:"absolute",bottom:24,left:"50%",transform:"translateX(-50%)" }}>
            <motion.div animate={{ y:[0,10,0] }} transition={{ repeat:Infinity,duration:1.5 }} style={{ color:"rgba(255,255,255,0.5)",fontSize:28 }}>↓</motion.div>
          </div>
        </div>
      </CurtainReveal>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   SECTION 2 — POSTS SHOWCASE
═══════════════════════════════════════════════════════════ */
const TAGS = ["Beauty","Fashion","Home Decor","Travel","Food & Drink","Fitness","Events"];
function S2Posts() {
  const isMobile = useIsMobile();
  return (
    <section className="snap-section">
      <CurtainReveal color="#0a0e1a">
        {/* Mobile: cards video fullscreen bg */}
        {isMobile && (
          <>
            <video autoPlay loop muted playsInline style={{ position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover" }} src={p("/video/cards.mp4")}/>
            <div style={{ position:"absolute",inset:0,background:"rgba(0,0,0,0.52)" }}/>
          </>
        )}
        {/* Desktop: split layout */}
        {!isMobile && (
          <div style={{ position:"absolute",inset:0,display:"flex" }}>
            <div style={{ width:"50%",position:"relative",flexShrink:0 }}>
              <video autoPlay loop muted playsInline style={{ position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover" }} src={p("/video/cards.mp4")}/>
              <div style={{ position:"absolute",inset:0,background:"rgba(0,0,0,0.18)" }}/>
            </div>
            <div style={{ flex:1,position:"relative" }}>
              <div style={{ position:"absolute",inset:0,backgroundImage:`url(${p("/Images/2bg.jpg")})`,backgroundSize:"cover",backgroundPosition:"center" }}/>
              <div style={{ position:"absolute",inset:0,background:"rgba(0,0,0,0.42)" }}/>
            </div>
          </div>
        )}
        <div style={{ position:"relative",zIndex:1,height:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"clamp(1rem,4vw,3rem)" }}>
          <h2 style={{ color:"#fff",fontSize:"clamp(1.3rem,3vw,2.4rem)",fontWeight:700,textAlign:"center",marginBottom:10 }}>Explore Your Niche</h2>
          <p style={{ color:"rgba(255,255,255,0.55)",fontSize:"clamp(0.85rem,1.5vw,0.95rem)",textAlign:"center",maxWidth:360,margin:"0 0 28px" }}>Connect with communities that match your passion</p>
          <div style={{ display:"flex",flexWrap:"wrap",gap:10,justifyContent:"center",maxWidth:520,padding:"0 1rem" }}>
            {TAGS.map((tag) => (
              <motion.button key={tag} whileHover={{ scale:1.1,y:-3 }}
                style={{ padding:"9px 18px",borderRadius:999,background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.18)",color:"#fff",fontSize:"clamp(0.78rem,1.2vw,0.88rem)",fontWeight:500,cursor:"pointer",backdropFilter:"blur(10px)" }}
                onMouseEnter={e=>e.currentTarget.style.background="rgba(0,212,255,0.22)"}
                onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.08)"}
              >{tag}</motion.button>
            ))}
          </div>
        </div>
      </CurtainReveal>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   SECTION 3 — QUOTES  (all 11 images, fast flash + brighter)
═══════════════════════════════════════════════════════════ */
const QUOTES = [
  { name:"Aria Lee",     handle:"@arialee", quote:"PicProfile changed how I see my feed — every scroll feels intentional." },
  { name:"Marco Reyes",  handle:"@marcor",  quote:"The AI analysis is uncanny. It knows my aesthetic better than I do." },
  { name:"Sophie Chen",  handle:"@sophiec", quote:"Brands actually reach out to me now. It's a game changer." },
  { name:"Kai Nakamura", handle:"@kain",    quote:"The board system is Pinterest but actually designed for creators." },
  { name:"Luna Vasquez", handle:"@lunav",   quote:"My engagement tripled in two months. No exaggeration." },
  { name:"Dev Patel",    handle:"@devp",    quote:"Clean, fast, and the notifications don't feel spammy. Rare." },
];
function QuoteCard({ name, handle, quote }) {
  return (
    <div style={{ minWidth:260,maxWidth:260,padding:"18px 16px",borderRadius:14,flexShrink:0,background:"rgba(0,0,0,0.48)",border:"1px solid rgba(255,255,255,0.14)",backdropFilter:"blur(12px)" }}>
      <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:10 }}>
        <div style={{ width:36,height:36,borderRadius:"50%",background:"linear-gradient(135deg,#2563eb,#00d4ff)",flexShrink:0 }}/>
        <div>
          <div style={{ color:"#fff",fontWeight:600,fontSize:"0.83rem" }}>{name}</div>
          <div style={{ color:"rgba(255,255,255,0.4)",fontSize:"0.73rem" }}>{handle}</div>
        </div>
      </div>
      <p style={{ color:"rgba(255,255,255,0.82)",fontSize:"0.83rem",lineHeight:1.6,margin:0 }}>"{quote}"</p>
    </div>
  );
}
function S3Quotes() {
  const [sRef, inView] = useInView(0.3);
  const imgIdx = useFastCrossfade(QUOTE_IMGS, 350);
  const trackRef = useRef(null);
  useEffect(() => {
    const el = trackRef.current; if (!el) return;
    let x = 0, raf;
    const tick = () => {
      x -= 0.7;
      if (Math.abs(x) >= el.scrollWidth / 2) x = 0;
      el.style.transform = `translateX(${x}px)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  const doubled = [...QUOTES, ...QUOTES];
  return (
    <section className="snap-section">
      <CurtainReveal color="#111">
        {QUOTE_IMGS.map((src, i) => (
          <img key={i} src={src} alt="" style={{ position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",objectPosition:"center",transition:"opacity 0.18s ease",opacity:i===imgIdx?1:0,filter:"brightness(1.25) saturate(1.1)" }}/>
        ))}
        <div style={{ position:"absolute",inset:0,background:"rgba(0,0,0,0.52)" }}/>
        <div ref={sRef} style={{ position:"relative",zIndex:1,height:"100%",display:"flex",flexDirection:"column",justifyContent:"center",overflow:"hidden" }}>
          <div style={{ textAlign:"center",padding:"0 1rem",marginBottom:"clamp(20px,4vh,48px)" }}>
            <p style={{ color:"rgba(255,255,255,0.92)",fontSize:"clamp(0.9rem,2.5vw,1.5rem)",fontWeight:300,letterSpacing:"clamp(1px,0.5vw,3px)",fontStyle:"italic" }}>
              <ScrambleText text="every thought defines your vision and emotion" inView={inView}/>
            </p>
          </div>
          <div style={{ overflow:"hidden",width:"100%" }}>
            <div ref={trackRef} style={{ display:"flex",gap:14,paddingBottom:8,width:"max-content" }}>
              {doubled.map((q, i) => <QuoteCard key={i} {...q}/>)}
            </div>
          </div>
        </div>
      </CurtainReveal>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   SECTION 4 — BRANDS  (responsive ring)
═══════════════════════════════════════════════════════════ */
const BRAND_NAMES = ["Chanel","Dior","Gucci","Prada","Versace","LV","Hermès","Balenciaga"];
function S4Brands() {
  const isMobile = useIsMobile();
  const circleRef = useRef(null);
  const btnRef    = useRef(null);
  const R = isMobile ? 88 : 130;

  useEffect(() => {
    const el = circleRef.current; if (!el) return;
    let angle = 0, raf;
    const spin = () => { angle += 0.2; el.style.transform = `rotate(${angle}deg)`; raf = requestAnimationFrame(spin); };
    raf = requestAnimationFrame(spin);
    return () => cancelAnimationFrame(raf);
  }, []);

  const onBtnMove = useCallback((e) => {
    const b = btnRef.current; if (!b) return;
    const r = b.getBoundingClientRect();
    b.style.transform = `translate(${(e.clientX-(r.left+r.width/2))*0.25}px,${(e.clientY-(r.top+r.height/2))*0.25}px)`;
  }, []);

  const containerSize = R * 2 + (isMobile ? 56 : 80);
  return (
    <section className="snap-section">
      <CurtainReveal color="#0a0e1a">
        <video autoPlay loop muted playsInline style={{ position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover" }} src={p("/video/newyork.mp4")}/>
        <div style={{ position:"absolute",inset:0,background:"rgba(0,0,0,0.50)" }}/>
        <div style={{ position:"relative",zIndex:1,height:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center" }}>
          <h2 style={{ color:"#fff",fontSize:"clamp(1.2rem,3vw,2.4rem)",fontWeight:700,marginBottom:isMobile?36:60,textAlign:"center",padding:"0 1rem" }}>Partner Brands</h2>
          <div style={{ position:"relative",width:containerSize,height:containerSize,flexShrink:0 }}>
            <div ref={circleRef} style={{ position:"absolute",inset:0,willChange:"transform" }}>
              {BRAND_NAMES.map((name, i) => {
                const a = (i / BRAND_NAMES.length) * Math.PI * 2;
                const half = containerSize / 2;
                return (
                  <div key={name} style={{ position:"absolute",left:Math.cos(a)*R+half,top:Math.sin(a)*R+half,transform:"translate(-50%,-50%)",background:"rgba(255,255,255,0.1)",border:"1px solid rgba(255,255,255,0.22)",backdropFilter:"blur(8px)",borderRadius:8,padding:isMobile?"4px 9px":"6px 12px",color:"#fff",fontSize:isMobile?"0.65rem":"0.75rem",fontWeight:600,letterSpacing:"1px",whiteSpace:"nowrap" }}>
                    {name}
                  </div>
                );
              })}
            </div>
            <div style={{ position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center" }}
              onMouseMove={onBtnMove} onMouseLeave={()=>{ if(btnRef.current) btnRef.current.style.transform="translate(0,0)"; }}>
              <button ref={btnRef} style={{ padding:isMobile?"12px 24px":"16px 36px",borderRadius:999,cursor:"pointer",background:"rgba(255,255,255,0.1)",border:"1px solid rgba(255,255,255,0.28)",color:"#fff",fontWeight:600,fontSize:isMobile?"0.88rem":"1rem",backdropFilter:"blur(12px)",transition:"transform 0.2s ease, box-shadow 0.2s ease" }}
                onMouseEnter={e=>e.currentTarget.style.boxShadow="0 0 30px rgba(0,212,255,0.35)"}
                onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}
              >Add Brands</button>
            </div>
          </div>
        </div>
      </CurtainReveal>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   SECTION 5 — 3D PANELS  (horizontal scroll on mobile)
═══════════════════════════════════════════════════════════ */
function TiltCard({ src }) {
  const ref    = useRef(null);
  const vidRef = useRef(null);
  const onMove  = (e) => { const r = ref.current.getBoundingClientRect(); ref.current.style.transform = `perspective(800px) rotateX(${((e.clientY-r.top)/r.height-0.5)*18}deg) rotateY(${((e.clientX-r.left)/r.width-0.5)*-18}deg) scale(1.04)`; };
  const onLeave = () => { ref.current.style.transform = "perspective(800px) rotateX(0) rotateY(0) scale(1)"; vidRef.current?.pause(); };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} onMouseEnter={()=>vidRef.current?.play()}
      style={{ width:"clamp(140px,28vw,280px)",aspectRatio:"9/16",borderRadius:20,overflow:"hidden",border:"1px solid rgba(255,255,255,0.12)",transition:"transform 0.15s ease",willChange:"transform",background:"#111",cursor:"pointer",flexShrink:0 }}>
      <video ref={vidRef} loop muted playsInline preload="none" style={{ width:"100%",height:"100%",objectFit:"cover",display:"block" }} src={p(`/video/${src}`)}/>
    </div>
  );
}
function S5Panels() {
  return (
    <section className="snap-section">
      <CurtainReveal color="#07090f">
        <div style={{ position:"absolute",inset:0,background:"#07090f",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"clamp(16px,3vw,28px)",padding:"clamp(1rem,3vw,2rem) 1rem" }}>
          <h2 style={{ color:"#fff",fontSize:"clamp(1.2rem,3vw,2.4rem)",fontWeight:700,textAlign:"center",margin:0 }}>Hover to Preview</h2>
          <p style={{ color:"rgba(255,255,255,0.4)",fontSize:"clamp(0.8rem,1.2vw,0.9rem)",margin:0,textAlign:"center" }}>3D moments — your stories in motion</p>
          {/* horizontal scroll on mobile, flex-wrap on desktop */}
          <div style={{ display:"flex",gap:"clamp(12px,2vw,24px)",justifyContent:"center",alignItems:"center",overflowX:"auto",width:"100%",paddingBottom:8,scrollbarWidth:"none",WebkitOverflowScrolling:"touch" }}>
            {["3D1.mp4","3D2.mp4","3D3.mp4"].map(s => <TiltCard key={s} src={s}/>)}
          </div>
        </div>
      </CurtainReveal>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   SECTION 6 — FEATURES  (car.mp4 bg)
═══════════════════════════════════════════════════════════ */
function WordReveal({ text, inView, delay = 0 }) {
  return (
    <span style={{ display:"inline-flex",flexWrap:"wrap",gap:"0.3em" }}>
      {text.split(" ").map((w, i) => (
        <motion.span key={i} initial={{ opacity:0,y:32 }} animate={inView?{opacity:1,y:0}:{}}
          transition={{ duration:0.55,delay:delay+i*0.09 }} style={{ display:"inline-block" }}>{w}</motion.span>
      ))}
    </span>
  );
}
function S6Features() {
  const [ref, inView] = useInView(0.3);
  return (
    <section className="snap-section">
      <CurtainReveal color="#0a0e1a">
        <video autoPlay loop muted playsInline style={{ position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover" }} src={p("/video/car.mp4")}/>
        <div style={{ position:"absolute",inset:0,background:"rgba(0,0,0,0.52)" }}/>
        <div ref={ref} style={{ position:"relative",zIndex:1,height:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"2rem 1.5rem" }}>
          <p style={{ color:"rgba(255,255,255,0.45)",letterSpacing:"4px",fontSize:"clamp(0.7rem,1vw,0.78rem)",textTransform:"uppercase",marginBottom:20,textAlign:"center" }}>Built for creators</p>
          <h2 style={{ fontSize:"clamp(2.2rem,7vw,6rem)",fontWeight:900,lineHeight:1.05,textAlign:"center",margin:0 }}>
            <div style={{ color:"#fff" }}><WordReveal text="Start Smart," inView={inView} delay={0}/></div>
            <div style={{ color:"rgba(255,255,255,0.32)" }}><WordReveal text="Start Small." inView={inView} delay={0.3}/></div>
          </h2>
        </div>
      </CurtainReveal>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   SECTION 7 — LIFESTYLE  (places.mp4)
═══════════════════════════════════════════════════════════ */
function S7Lifestyle() {
  return (
    <section className="snap-section">
      <CurtainReveal color="#0a0e1a">
        <video autoPlay loop muted playsInline style={{ position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover" }} src={p("/video/places.mp4")}/>
        <div style={{ position:"absolute",inset:0,background:"rgba(0,0,0,0.38)" }}/>
        <div style={{ position:"relative",zIndex:1,height:"100%",display:"flex",alignItems:"center",justifyContent:"center" }}>
          <h2 style={{ fontSize:"clamp(2rem,7vw,6rem)",fontWeight:800,color:"#fff",textAlign:"center",fontFamily:"Georgia,serif",letterSpacing:"clamp(1px,1vw,3px)",textShadow:"0 0 40px rgba(255,255,255,0.22)",padding:"0 1rem" }}>
            Show Your Lifestyle
          </h2>
        </div>
      </CurtainReveal>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   SECTION 8 — DASHBOARD  (cash.mp4 + responsive bento grid)
═══════════════════════════════════════════════════════════ */
const BENTO_CARDS = [
  { label:"Followers",       value:"12.4K", desktopSpan:"span 2", emoji:"👥" },
  { label:"Impressions",     value:"89.2K", desktopSpan:"span 2", emoji:"👁" },
  { label:"Revenue",         value:"$2,450",desktopSpan:"span 1", emoji:"💰" },
  { label:"Link Clicks",     value:"5,230", desktopSpan:"span 1", emoji:"🔗" },
  { label:"Engagement Rate", value:"4.8%",  desktopSpan:"span 1", emoji:"📈" },
  { label:"Top Post Reach",  value:"23K",   desktopSpan:"span 1", emoji:"🚀" },
];
function MagicBentoCard({ label, value, desktopSpan, emoji, mousePos, isMobile }) {
  const cardRef = useRef(null);
  const [glow, setGlow] = useState({ x:"50%",y:"50%",op:0 });
  useEffect(() => {
    const el = cardRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const x = mousePos.x - r.left, y = mousePos.y - r.top;
    if (x>=0 && x<=r.width && y>=0 && y<=r.height) setGlow({ x:`${(x/r.width)*100}%`,y:`${(y/r.height)*100}%`,op:1 });
    else setGlow(g=>({...g,op:0}));
  }, [mousePos]);
  return (
    <motion.div ref={cardRef}
      initial={{ opacity:0,y:20 }} whileInView={{ opacity:1,y:0 }} viewport={{ once:true }}
      whileHover={{ y:-5 }}
      style={{ gridColumn:isMobile?"span 1":desktopSpan,position:"relative",padding:isMobile?"18px 14px":"26px 22px",borderRadius:16,overflow:"hidden",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",backdropFilter:"blur(16px)",transition:"box-shadow 0.2s" }}
      onMouseEnter={e=>e.currentTarget.style.boxShadow="0 8px 32px rgba(0,212,255,0.18)"}
      onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}
    >
      <div style={{ position:"absolute",inset:0,pointerEvents:"none",borderRadius:16,background:`radial-gradient(circle at ${glow.x} ${glow.y}, rgba(0,212,255,0.2) 0%, transparent 60%)`,opacity:glow.op,transition:"opacity 0.15s" }}/>
      <div style={{ position:"relative" }}>
        <div style={{ fontSize:isMobile?"1.1rem":"1.4rem",marginBottom:8 }}>{emoji}</div>
        <div style={{ color:"rgba(255,255,255,0.42)",fontSize:isMobile?"0.65rem":"0.72rem",letterSpacing:"1.5px",textTransform:"uppercase",marginBottom:8 }}>{label}</div>
        <div style={{ color:"#fff",fontSize:isMobile?"clamp(1.2rem,4vw,1.6rem)":"clamp(1.6rem,3vw,2.4rem)",fontWeight:800 }}>{value}</div>
      </div>
    </motion.div>
  );
}
function S8Dashboard() {
  const isMobile = useIsMobile();
  const [mousePos, setMousePos] = useState({ x:0,y:0 });
  return (
    <section className="snap-section" onMouseMove={e=>setMousePos({ x:e.clientX,y:e.clientY })}>
      <CurtainReveal color="#060912">
        <video autoPlay loop muted playsInline style={{ position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover" }} src={p("/video/cash.mp4")}/>
        <div style={{ position:"absolute",inset:0,background:"rgba(0,0,0,0.62)" }}/>
        <div style={{ position:"relative",zIndex:1,height:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"clamp(1rem,3vw,2rem)" }}>
          <div style={{ width:"100%",maxWidth:860 }}>
            <div style={{ marginBottom:isMobile?18:32,textAlign:"center" }}>
              <p style={{ color:"rgba(255,255,255,0.4)",fontSize:"clamp(0.65rem,1vw,0.78rem)",letterSpacing:"3px",textTransform:"uppercase",marginBottom:8 }}>Creator Analytics</p>
              <h2 style={{ color:"#fff",fontSize:"clamp(1.2rem,3.5vw,2.6rem)",fontWeight:800,margin:0 }}>Your Growth at a Glance</h2>
            </div>
            <div style={{ display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:isMobile?10:14 }}>
              {BENTO_CARDS.map((c) => <MagicBentoCard key={c.label} {...c} mousePos={mousePos} isMobile={isMobile}/>)}
            </div>
          </div>
        </div>
      </CurtainReveal>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   SECTION 9 — PROFILE  (all 9 images fast flash + brighter)
═══════════════════════════════════════════════════════════ */
function S9Profile() {
  const imgIdx = useFastCrossfade(PROFILE_IMGS, 360);
  return (
    <section className="snap-section">
      <CurtainReveal color="#0a0e1a">
        {PROFILE_IMGS.map((src, i) => (
          <img key={i} src={src} alt="" style={{ position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",objectPosition:"center",transition:"opacity 0.18s ease",opacity:i===imgIdx?1:0,filter:"brightness(1.2) saturate(1.1)" }}/>
        ))}
        <div style={{ position:"absolute",inset:0,background:"rgba(0,0,0,0.44)" }}/>
        <div style={{ position:"relative",zIndex:1,height:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",textAlign:"center",padding:"2rem" }}>
          <h2 style={{ color:"#fff",fontWeight:800,fontSize:"clamp(1.4rem,3vw,2.4rem)",margin:"0 0 6px" }}>Your Name</h2>
          <p style={{ color:"rgba(255,255,255,0.55)",fontSize:"clamp(0.88rem,1.5vw,1rem)",margin:"0 0 18px" }}>@yourhandle</p>
          <p style={{ color:"rgba(255,255,255,0.72)",fontSize:"clamp(0.88rem,1.8vw,1.05rem)",lineHeight:1.7,maxWidth:420,margin:0 }}>
            Your bio goes here. Tell your story, your aesthetic, your world. Make it yours.
          </p>
        </div>
      </CurtainReveal>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   SECTION 10 — VISUAL
═══════════════════════════════════════════════════════════ */
function S10Visual() {
  return (
    <section className="snap-section">
      <CurtainReveal color="#0a0e1a">
        <div style={{ position:"absolute",inset:0,backgroundImage:`url(${STAY_IMG})`,backgroundSize:"cover",backgroundPosition:"center",filter:"brightness(1.15)" }}/>
        <div style={{ position:"absolute",inset:0,background:"rgba(0,0,0,0.38)" }}/>
        <div style={{ position:"relative",zIndex:1,height:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",textAlign:"center",padding:"2rem 1.5rem",gap:16 }}>
          <h2 style={{ fontSize:"clamp(1.6rem,5vw,4rem)",fontWeight:900,color:"#fff",letterSpacing:"clamp(2px,1vw,4px)",margin:0,fontFamily:"Georgia,serif",textShadow:"0 0 40px rgba(255,255,255,0.2)" }}>
            all you had to do was stay
          </h2>
          <p style={{ color:"rgba(255,255,255,0.75)",fontSize:"clamp(0.85rem,2.2vw,1.4rem)",letterSpacing:"clamp(2px,1.5vw,6px)",textTransform:"uppercase",margin:0 }}>make your profile</p>
          <motion.a href="/auth" whileHover={{ scale:1.05 }} style={{ marginTop:8,padding:"13px 36px",borderRadius:999,background:"rgba(255,255,255,0.12)",border:"1px solid rgba(255,255,255,0.3)",backdropFilter:"blur(10px)",color:"#fff",fontWeight:600,fontSize:"clamp(0.82rem,1.5vw,0.95rem)",letterSpacing:"2px",textDecoration:"none",textTransform:"uppercase" }}>
            Start Now
          </motion.a>
        </div>
      </CurtainReveal>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   SECTION 11 — FOUNDER  (responsive: side-by-side ↔ stacked)
═══════════════════════════════════════════════════════════ */
function S11Founder() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [ref, inView] = useInView(0.3);
  return (
    <section className="snap-section">
      <CurtainReveal color="#000">
        {/* CEO image — always visible (bg on mobile, left panel on desktop) */}
        <img src={p("/Images/ceo.jpg")} alt="Founder" style={{
          position:"absolute", top:0, left:0,
          width: isMobile ? "100%" : "48%",
          height: "100%",
          objectFit:"cover", objectPosition:"top center",
          filter:"grayscale(80%) contrast(1.1) brightness(1.35)",
        }}/>
        {/* Gradient overlay */}
        <div style={{
          position:"absolute", inset:0,
          background: isMobile
            ? "linear-gradient(to bottom, rgba(0,0,0,0.15) 30%, rgba(0,0,0,0.88) 70%)"
            : "linear-gradient(to right, transparent 30%, #000 52%)",
        }}/>

        {/* Mobile: centered at bottom — Desktop: right side */}
        <div ref={ref} style={{
          position:"absolute",
          ...(isMobile
            ? { bottom:0, left:0, right:0, padding:"0 1.5rem 2.5rem" }
            : { top:0, right:0, width:"52%", height:"100%", display:"flex", flexDirection:"column", justifyContent:"center", padding:"clamp(2rem,5vw,5rem)" }
          ),
        }}>
          <motion.div initial={{ y:isMobile?24:0, x:isMobile?0:40, opacity:0 }} animate={inView?{x:0,y:0,opacity:1}:{}} transition={{ duration:0.8 }}>
            <p style={{ color:"rgba(255,255,255,0.45)",fontSize:"0.7rem",letterSpacing:"3px",textTransform:"uppercase",marginBottom:isMobile?10:24 }}>From the founder</p>
            <blockquote style={{ color:"#fff",fontSize:"clamp(0.92rem,2.2vw,1.6rem)",fontFamily:"Georgia,serif",lineHeight:1.6,fontStyle:"italic",margin:isMobile?"0 0 14px":"0 0 28px",borderLeft:isMobile?"none":"2px solid rgba(255,255,255,0.2)",paddingLeft:isMobile?0:24 }}>
              "Building the future of visual storytelling..."
            </blockquote>
            <div style={{ color:"#fff",fontSize:"clamp(1rem,2.5vw,1.9rem)",fontWeight:800,letterSpacing:"1px",marginBottom:4 }}>Aman Singh</div>
            <div style={{ color:"rgba(255,255,255,0.45)",fontSize:"clamp(0.78rem,1.2vw,0.9rem)",marginBottom:isMobile?20:40 }}>Founder & CEO</div>
            <motion.button whileHover={{ scale:1.05 }} onClick={()=>navigate("/auth")}
              style={{ padding:isMobile?"12px 32px":"18px 52px",borderRadius:999,cursor:"pointer",background:"#fff",color:"#000",fontWeight:700,fontSize:"clamp(0.85rem,1.2vw,1rem)",border:"2px solid #fff",letterSpacing:"1px",transition:"background 0.25s, color 0.25s, box-shadow 0.25s" }}
              onMouseEnter={e=>{e.currentTarget.style.background="#000";e.currentTarget.style.color="#fff";e.currentTarget.style.boxShadow="0 0 30px rgba(255,255,255,0.2)";}}
              onMouseLeave={e=>{e.currentTarget.style.background="#fff";e.currentTarget.style.color="#000";e.currentTarget.style.boxShadow="none";}}
            >Create Account</motion.button>
          </motion.div>
        </div>
      </CurtainReveal>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
const SECTIONS = [S1Hero,S2Posts,S3Quotes,S4Brands,S5Panels,S6Features,S7Lifestyle,S8Dashboard,S9Profile,S10Visual,S11Founder];

export default function LandingPage() {
  const [current, setCurrent] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const els = containerRef.current?.querySelectorAll(".snap-section");
    if (!els) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) setCurrent(Array.from(els).indexOf(e.target)); });
    }, { threshold:0.5, root:containerRef.current });
    els.forEach(s => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <style>{`
        .snap-container {
          height: 100vh; overflow-y: scroll;
          scroll-snap-type: y mandatory;
          scrollbar-width: none; -ms-overflow-style: none;
        }
        .snap-container::-webkit-scrollbar { display: none; }
        .snap-section {
          height: 100vh; scroll-snap-align: start;
          scroll-snap-stop: always; flex-shrink: 0;
          position: relative; overflow: hidden;
        }
        * { box-sizing: border-box; }
        @media (pointer: fine) {
          html, body, * { cursor: none !important; }
        }
      `}</style>

      <UnseenCursor />
      <PageDots total={SECTIONS.length} current={current} />

      <div ref={containerRef} className="snap-container">
        {SECTIONS.map((Comp, i) => <Comp key={i} />)}
      </div>
    </>
  );
}
